import React, { useMemo } from 'react';
import Grid from "@mui/material/Grid";
import { textfieldDateFormat, shiftHours, dateFormat } from "../../utils/dateFunctions";
import { getAPIDataParamsReturn } from "../../utils/apiFunction";
import { numberFormat } from '../../utils/numberFunctions';
import SelectWarehouse from '../../components/SelectWarehouse';
import SupplierSelect from '../../components/SupplierSelect';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Box, Button, Chip, Stack, TableRow, Table, TableCell, TableHead, TableBody, TableContainer, Tooltip as MuiTooltip, Typography, CircularProgress, Paper } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, ResponsiveContainer } from 'recharts';
import BarChartIcon from '@mui/icons-material/BarChart';
import DownloadIcon from '@mui/icons-material/Download';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TableRowsIcon from '@mui/icons-material/TableRows';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import DateSelector from 'app/shared/DateSelector';

const HalfshellInOuts = (props) => {
    const [startDate, setStartDate] = React.useState(shiftHours(new Date(), -24 * 30));
    const [endDate, setEndDate] = React.useState(new Date());
    const [location, setLocation] = React.useState(null);
    const [display, setDisplay] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [data, setData] = React.useState(null);
    const [showChart, setShowChart] = React.useState(true);
    const [tableLayout, setTableLayout] = React.useState('day');
    const [exporting, setExporting] = React.useState(false);
    const [supplierType, setSupplierType] = React.useState('both');
    const [supplier, setSupplier] = React.useState(null);

    const processSections = [
        { label: 'Halfshell', isHalfshell: true },
        { label: 'Live', isHalfshell: false },
    ];
    const filteredData = useMemo(() => (data || []).filter((entry) => {
            if (supplier && entry.supplierId !== supplier.id) {
                return false;
            }
            if (supplierType === 'both') {
                return true;
            }
            if (supplierType === 'internal') {
                return entry.supplier?.isInternal === true;
            }
            return entry.supplier?.isInternal === false;
        }), [data, supplier, supplierType]);
    const totals = useMemo(() => filteredData.reduce((summary, entry) => ({
        input: summary.input + (Number(entry.input) || 0),
        output: summary.output + (Number(entry.output) || 0),
    }), { input: 0, output: 0 }), [filteredData]);
    const dateCount = useMemo(() => new Set(filteredData.map((entry) => entry.date)).size, [filteredData]);


    React.useEffect(() => {
        if (location) {
            setLoading(true);
            getAPIDataParamsReturn('/Reports/HalfshellOutputHistory', { EndDate: textfieldDateFormat(endDate), StartDate: textfieldDateFormat(startDate), WarehouseId: location?.id }).then((dgs) => {
                setData(dgs);
                setLoading(false);
            });
        } else {
            setData(null);
            setLoading(false);
        }
    }, [location, startDate, endDate]);

    const organiseForChart = (dataU) => {
        if (dataU) {
            //get unique list of dates
            var dates = [];
            for (let i = 0; i < dataU.length; i++) {
                if (!dates.includes(dataU[i].date)) {
                    dates.push(dataU[i].date);
                }
            }
            //group data by date
            var groupedData = []
            for (let i = 0; i < dates.length; i++) {
                var newGroup = { Date: dates[i], Output: 0, Input: 0 };
                for (let j = 0; j < dataU.length; j++) {
                    if (dataU[j].date === dates[i]) {
                        newGroup.Output += dataU[j].output;
                        newGroup.Input += dataU[j].input;
                    }
                }
                groupedData.push(newGroup);
            }
            var chartData = [];
            for (let i = 0; i < groupedData.length; i++) {
                var obj;
                obj = { name: groupedData[i].Date, output: groupedData[i].Output, input: groupedData[i].Input, difference: groupedData[i].Output - groupedData[i].Input };
                chartData.push(obj);
            }
            return chartData;
        }
        return [];
    }
    const organiseForTable = (dataU) => {
        if (dataU) {
            //get unique list of dates
            var dates = [];
            for (let i = 0; i < dataU.length; i++) {
                if (!dates.includes(dataU[i].date)) {
                    dates.push(dataU[i].date);
                }
            }
            //group data by date
            var groupedData = []
            for (let i = 0; i < dates.length; i++) {
                var newGroup = { Date: dates[i], entries: [] };
                // collect all entries for this date
                const dayEntries = dataU.filter(d => d.date === dates[i]);
                // aggregate by mixedGroupId (or fallback key)
                const byGroup = new Map();
                dayEntries.forEach((e, entryIndex) => {
                    const mixedGroupId = e?.mixedGroupId || e?.MixedGroupId;
                    const key = mixedGroupId ? `mixed_${mixedGroupId}` : `single_${entryIndex}`;
                    if (!byGroup.has(key)) byGroup.set(key, []);
                    byGroup.get(key).push(e);
                });
                const aggregated = [];
                byGroup.forEach((list) => {
                    if (list.length === 1) {
                        const e = list[0];
                        aggregated.push({ ...e, __gradesBreakdown: null, __gradingFactor: e.gradingFactor });
                    } else {
                        const sample = list[0] || {};
                        const totalInput = list.reduce((p, c) => p + (Number(c.input) || 0), 0);
                        const totalOutput = list.reduce((p, c) => p + (Number(c.output) || 0), 0);
                        // build grade breakdown based on input proportions
                        const gradeMap = new Map();
                        list.forEach((c) => {
                            const label = (c?.mixedGrades?.name) || (c?.grade?.description) || (c?.stockItem?.grade?.description) || (c?.stockItem?.description) || 'Unknown';
                            const inp = Number(c.input) || 0;
                            gradeMap.set(label, (gradeMap.get(label) || 0) + inp);
                        });
                        const parts = Array.from(gradeMap.entries()).map(([label, inp]) => {
                            const pct = totalInput > 0 ? Math.round((inp / totalInput) * 100) : 0;
                            return `${label} ${pct}%`;
                        });
                        const breakdownText = parts.join(' | ');
                        const gradingFactorEntries = list.filter((entry) => Number.isFinite(Number(entry.gradingFactor)) && (Number(entry.output) || 0) > 0);
                        const gradingFactorQuantity = gradingFactorEntries.reduce((total, entry) => total + (Number(entry.output) || 0), 0);
                        const gradingFactor = gradingFactorQuantity > 0
                            ? gradingFactorEntries.reduce((total, entry) => total + (Number(entry.gradingFactor) * (Number(entry.output) || 0)), 0) / gradingFactorQuantity
                            : null;
                        aggregated.push({
                            ...sample,
                            input: totalInput,
                            output: totalOutput,
                            inProgress: list.some((entry) => entry.inProgress),
                            __gradesBreakdown: breakdownText,
                            __gradingFactor: gradingFactor,
                        });
                    }
                });
                newGroup.entries = aggregated;
                groupedData.push(newGroup);
            }

            return groupedData.sort((a, b) => new Date(b.Date) - new Date(a.Date));
        }
        return [];
    }
    const matchesVarianceFilter = (entry) => {
        const difference = (Number(entry.output) || 0) - (Number(entry.input) || 0);
        return display === 0 || (display === 1 && difference > 0) || (display === 2 && difference < 0);
    };
    const sortByDifference = (a, b) => {
        const differenceA = (Number(a.output) || 0) - (Number(a.input) || 0);
        const differenceB = (Number(b.output) || 0) - (Number(b.input) || 0);
        return differenceA - differenceB;
    };
    const combinedEntries = processSections.flatMap((section) => {
        const sectionData = filteredData.filter((entry) => Boolean(entry.isHalfshell) === section.isHalfshell);
        return organiseForTable(sectionData).flatMap((group) => group.entries.map((entry) => ({
            ...entry,
            __date: group.Date,
            __process: section.label,
        })));
    }).filter(matchesVarianceFilter).sort(sortByDifference);
    const combinedShowInProgress = combinedEntries.some((entry) => entry.inProgress);

    const exportCombinedTable = async () => {
        if (combinedEntries.length === 0) {
            return;
        }

        setExporting(true);
        try {
            const ExcelJS = (await import('exceljs')).default;
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Processing Ins and Outs');
            worksheet.columns = [
                { header: 'Date', key: 'date', width: 14 },
                { header: 'Process', key: 'process', width: 14 },
                { header: 'Grades', key: 'grades', width: 34 },
                { header: 'Supplier', key: 'supplier', width: 34 },
                { header: 'Lot Serial No.', key: 'lotSerialNbr', width: 28 },
                { header: 'Input', key: 'input', width: 14 },
                { header: 'Output', key: 'output', width: 14 },
                { header: 'Difference', key: 'difference', width: 14 },
                { header: 'Grading Factor', key: 'gradingFactor', width: 16 },
                { header: 'Status', key: 'status', width: 16 },
            ];
            worksheet.addRows(combinedEntries.map((entry) => ({
                date: dateFormat(entry.__date),
                process: entry.__process,
                grades: entry.__gradesBreakdown || entry?.stockItem?.description || '',
                supplier: entry.supplier?.name || '',
                lotSerialNbr: entry.lotSerialNbr || '',
                input: Number(entry.input) || 0,
                output: Number(entry.output) || 0,
                difference: (Number(entry.output) || 0) - (Number(entry.input) || 0),
                gradingFactor: Number.isFinite(Number(entry.__gradingFactor)) ? Number(entry.__gradingFactor) : null,
                status: entry.inProgress ? 'In progress' : 'Complete',
            })));
            worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
            worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3F6F8F' } };
            worksheet.getRow(1).alignment = { vertical: 'middle' };
            worksheet.autoFilter = { from: 'A1', to: 'J1' };
            worksheet.views = [{ state: 'frozen', ySplit: 1 }];
            ['F', 'G', 'H', 'I'].forEach((column) => {
                worksheet.getColumn(column).numFmt = '#,##0.00';
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `ProcessingInsOuts_${textfieldDateFormat(startDate)}_${textfieldDateFormat(endDate)}.xlsx`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Processing Ins and Outs Excel export failed', error);
        } finally {
            setExporting(false);
        }
    };

    return (
        <Box sx={{ pb: 4 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 2.5 }}>
                <Box>
                    <Typography variant="h2" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Processing Ins &amp; Outs
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {location ? location.description : 'Select a warehouse to view processing performance'}
                    </Typography>
                </Box>
                <ToggleButtonGroup
                    value={showChart ? 'charts' : 'table'}
                    color="primary"
                    exclusive
                    size="small"
                    onChange={(event, newValue) => {
                        if (newValue) {
                            setShowChart(newValue === 'charts');
                        }
                    }}
                    aria-label="report view"
                >
                    <ToggleButton value="charts" aria-label="chart view">
                        <BarChartIcon fontSize="small" sx={{ mr: 1 }} /> Charts
                    </ToggleButton>
                    <ToggleButton value="table" aria-label="table view">
                        <TableRowsIcon fontSize="small" sx={{ mr: 1 }} /> Table
                    </ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={6} lg={3}>
                        <DateSelector onlyCustom startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} label="Date" />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <SelectWarehouse access={props.access} enforceAccess={true} state={location} setState={setLocation} label="Warehouse" all useCache />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <SupplierSelect widthAuto state={supplier} setState={setSupplier} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <Stack spacing={0.75}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Supplier type</Typography>
                            <ToggleButtonGroup
                                value={supplierType}
                                color="primary"
                                exclusive
                                size="small"
                                fullWidth
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        setSupplierType(newValue);
                                    }
                                }}
                                aria-label="supplier type"
                            >
                                <ToggleButton value="both">Both</ToggleButton>
                                <ToggleButton value="internal">Internal</ToggleButton>
                                <ToggleButton value="external">External</ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {location ? <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
                <Grid container>
                    <SummaryMetric label="Total input" value={numberFormat(totals.input)} />
                    <SummaryMetric label="Total output" value={numberFormat(totals.output)} />
                    <SummaryMetric label="Net variance" value={numberFormat(totals.output - totals.input)} valueColor={totals.output - totals.input < 0 ? 'error.main' : 'success.main'} />
                    <SummaryMetric label="Days reported" value={dateCount} last />
                </Grid>
            </Paper> : null}

            {!location ? <Paper variant="outlined" sx={{ py: 8, px: 3, textAlign: 'center' }}>
                <WarehouseOutlinedIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="h5" sx={{ mb: 0.5 }}>Choose a warehouse</Typography>
                <Typography variant="body2" color="text.secondary">Report data will appear here once a warehouse is selected.</Typography>
            </Paper> : loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box> : (showChart ? <Grid container spacing={3}>{processSections.map((section) => {
                    const sectionData = filteredData.filter((entry) => Boolean(entry.isHalfshell) === section.isHalfshell);
                    const chartData = organiseForChart(sectionData);

                    return (<Grid item xs={12} key={section.label}>
                        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>{section.label}</Typography>
                                <Chip label={`${chartData.length} days`} size="small" variant="outlined" />
                            </Stack>
                            {chartData.length === 0 ? <EmptySection /> : <Grid container spacing={3}>
                                <Grid item xs={12} xl={6}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Input and output</Typography>
                                    <Box sx={{ width: '100%', height: 340 }}>
                                        <ResponsiveContainer>
                                            <BarChart data={chartData} margin={{ top: 10, right: 12, left: 4, bottom: 4 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                <XAxis dataKey="name" tickFormatter={dateFormat} tick={{ fontSize: 12 }} />
                                                <YAxis tickFormatter={numberFormat} tick={{ fontSize: 12 }} width={64} />
                                                <Tooltip labelFormatter={dateFormat} formatter={numberFormat} />
                                                <Legend />
                                                <Bar dataKey="input" name="Input" fill="#3f6f8f" radius={[3, 3, 0, 0]} />
                                                <Bar dataKey="output" name="Output" fill="#3b8f6a" radius={[3, 3, 0, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} xl={6}>
                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Net variance</Typography>
                                    <Box sx={{ width: '100%', height: 340 }}>
                                        <ResponsiveContainer>
                                            <BarChart data={chartData} margin={{ top: 10, right: 12, left: 4, bottom: 4 }}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                <XAxis dataKey="name" tickFormatter={dateFormat} tick={{ fontSize: 12 }} />
                                                <YAxis tickFormatter={numberFormat} tick={{ fontSize: 12 }} width={64} />
                                                <Tooltip labelFormatter={dateFormat} formatter={numberFormat} />
                                                <Bar dataKey="difference" name="Output - input" radius={[3, 3, 0, 0]}>
                                                    {chartData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.difference >= 0 ? '#3b8f6a' : '#c85353'} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Grid>
                            </Grid>}
                        </Paper>
                    </Grid>)
                })}</Grid> : <Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Showing {filteredData.length} processing records</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <ToggleButtonGroup
                                value={tableLayout}
                                color="primary"
                                exclusive
                                size="small"
                                onChange={(event, newValue) => {
                                    if (newValue) {
                                        setTableLayout(newValue);
                                    }
                                }}
                                aria-label="table layout"
                            >
                                <ToggleButton value="day">By day</ToggleButton>
                                <ToggleButton value="combined">One table</ToggleButton>
                            </ToggleButtonGroup>
                            <ToggleButtonGroup
                                value={display}
                                color="primary"
                                exclusive
                                size="small"
                                onChange={(event, newValue) => {
                                    if (newValue !== null) {
                                        setDisplay(newValue);
                                    }
                                }}
                                aria-label="variance filter"
                            >
                                <ToggleButton value={0}>All</ToggleButton>
                                <ToggleButton value={1}>Gains</ToggleButton>
                                <ToggleButton value={2}>Losses</ToggleButton>
                            </ToggleButtonGroup>
                        </Stack>
                    </Stack>
                    {tableLayout === 'combined' ? <CombinedTable entries={combinedEntries} showInProgress={combinedShowInProgress} exporting={exporting} onExport={exportCombinedTable} /> : Array.from(new Set(filteredData.map((entry) => entry.date)))
                    .sort((a, b) => new Date(b) - new Date(a))
                    .map((date) => {
                        return (<Box key={date} sx={{ mb: 3.5 }}>
                            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>{dateFormat(date)}</Typography>
                            <Grid container spacing={2}>
                            {processSections.map((section) => {
                                const sectionData = filteredData.filter((entry) => entry.date === date && Boolean(entry.isHalfshell) === section.isHalfshell);
                                const entries = organiseForTable(sectionData)[0]?.entries || [];
                                if (entries.length === 0) {
                                    return null;
                                }
                                const showInProgress = entries.some((entry) => entry.inProgress);

                                return (<Grid item xs={12} key={`${date}-${section.label}`}>
                                <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                                        <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>{section.label}</Typography>
                                        <Chip label={`${entries.length} ${entries.length === 1 ? 'record' : 'records'}`} size="small" variant="outlined" />
                                    </Stack>
                                    <TableContainer>
                                    <Table size="small" sx={{ minWidth: 900 }}>
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                                <TableCell>Grades</TableCell>
                                                <TableCell>Supplier</TableCell>
                                                <TableCell>Lot Serial No.</TableCell>
                                                <TableCell align="right">Input</TableCell>
                                                <TableCell align="right">Output</TableCell>
                                                <TableCell align="right">Difference</TableCell>
                                                <TableCell align="right"><GradingFactorHeader /></TableCell>
                                                {showInProgress ? <TableCell>In Progress</TableCell> : null}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {entries.filter(matchesVarianceFilter).sort(sortByDifference).map((entry, entryIndex) => {
                                                const difference = (Number(entry.output) || 0) - (Number(entry.input) || 0);
                                                const colourStrength = Math.min(Math.abs(difference) / 30, 1);
                                                const varianceColour = difference > 0 ? '#2e7d32' : difference < 0 ? '#d32f2f' : null;
                                                const rowColour = varianceColour ? alpha(varianceColour, colourStrength * 0.4) : 'transparent';
                                                const hoverColour = varianceColour ? alpha(varianceColour, Math.min((colourStrength * 0.4) + 0.06, 0.46)) : 'action.hover';

                                                return (<TableRow key={`${entry.lotSerialNbr}-${entry.stockItemId}-${entryIndex}`} hover sx={{ backgroundColor: rowColour, '&:hover': { backgroundColor: hoverColour } }}>
                                                    <TableCell>{entry.__gradesBreakdown ? entry.__gradesBreakdown : ( entry?.stockItem?.description || '')}</TableCell>
                                                    <TableCell>{entry.supplier?.name}</TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.lotSerialNbr}</TableCell>
                                                    <TableCell align="right">{numberFormat(entry.input)}</TableCell>
                                                    <TableCell align="right">{numberFormat(entry.output)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{numberFormat(difference)}</TableCell>
                                                    <TableCell align="right">{formatGradingFactor(entry.__gradingFactor)}</TableCell>
                                                    {showInProgress ? <TableCell><Chip label={entry.inProgress ? 'In progress' : 'Complete'}  size="small" variant="outlined" /></TableCell> : null}
                                                </TableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                    </TableContainer>
                                </Paper>
                                </Grid>)
                            })}
                            </Grid>
                        </Box>)
                    })}
                </Box>)}
        </Box>
    );
};

const CombinedTable = ({ entries, showInProgress, exporting, onExport }) => (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Box>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>All processing records</Typography>
                <Typography variant="caption" color="text.secondary">{entries.length} {entries.length === 1 ? 'record' : 'records'}</Typography>
            </Box>
            <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={onExport} disabled={entries.length === 0 || exporting}>
                {exporting ? 'Exporting...' : 'Export Excel'}
            </Button>
        </Stack>
        {entries.length === 0 ? <EmptySection compact /> : <TableContainer>
            <Table size="small" sx={{ minWidth: 1100 }}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                        <TableCell>Date</TableCell>
                        <TableCell>Process</TableCell>
                        <TableCell>Grades</TableCell>
                        <TableCell>Supplier</TableCell>
                        <TableCell>Lot Serial No.</TableCell>
                        <TableCell align="right">Input</TableCell>
                        <TableCell align="right">Output</TableCell>
                        <TableCell align="right">Difference</TableCell>
                        <TableCell align="right"><GradingFactorHeader /></TableCell>
                        {showInProgress ? <TableCell>In Progress</TableCell> : null}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {entries.map((entry, entryIndex) => {
                        const difference = (Number(entry.output) || 0) - (Number(entry.input) || 0);
                        const colourStrength = Math.min(Math.abs(difference) / 30, 1);
                        const varianceColour = difference > 0 ? '#2e7d32' : difference < 0 ? '#d32f2f' : null;
                        const rowColour = varianceColour ? alpha(varianceColour, colourStrength * 0.4) : 'transparent';
                        const hoverColour = varianceColour ? alpha(varianceColour, Math.min((colourStrength * 0.4) + 0.06, 0.46)) : 'action.hover';

                        return (<TableRow key={`${entry.__date}-${entry.__process}-${entry.lotSerialNbr}-${entry.stockItemId}-${entryIndex}`} hover sx={{ backgroundColor: rowColour, '&:hover': { backgroundColor: hoverColour } }}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFormat(entry.__date)}</TableCell>
                            <TableCell>{entry.__process}</TableCell>
                            <TableCell>{entry.__gradesBreakdown || entry?.stockItem?.description || ''}</TableCell>
                            <TableCell>{entry.supplier?.name}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.lotSerialNbr}</TableCell>
                            <TableCell align="right">{numberFormat(entry.input)}</TableCell>
                            <TableCell align="right">{numberFormat(entry.output)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{numberFormat(difference)}</TableCell>
                            <TableCell align="right">{formatGradingFactor(entry.__gradingFactor)}</TableCell>
                            {showInProgress ? <TableCell><Chip label={entry.inProgress ? 'In progress' : 'Complete'} size="small" variant="outlined" /></TableCell> : null}
                        </TableRow>);
                    })}
                </TableBody>
            </Table>
        </TableContainer>}
    </Paper>
);

const GradingFactorHeader = () => (
    <MuiTooltip title="Positive means the output was upgraded, negative means it was downgraded, and zero means the grade was unchanged." arrow>
        <Stack component="span" direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ cursor: 'help', whiteSpace: 'nowrap' }}>
            <span>Grading Factor</span>
            <HelpOutlineIcon sx={{ fontSize: 16 }} />
        </Stack>
    </MuiTooltip>
);

const formatGradingFactor = (value) => Number.isFinite(Number(value)) ? Number(value).toFixed(2) : '—';

const SummaryMetric = ({ label, value, valueColor = 'text.primary', last = false }) => (
    <Grid item xs={6} md={3} sx={{ px: 2.5, py: 2, borderRight: { xs: 0, md: last ? 0 : 1 }, borderBottom: { xs: 1, md: 0 }, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.25 }}>{label}</Typography>
        <Typography variant="h5" sx={{ color: valueColor, fontWeight: 700 }}>{value}</Typography>
    </Grid>
);

const EmptySection = ({ compact = false }) => (
    <Box sx={{ px: 2, py: compact ? 2.5 : 7, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">No processing records for this period.</Typography>
    </Box>
);

export default HalfshellInOuts;


